import Phaser from 'phaser';
import { COLORS, DEPTHS, FONTS, GAME_HEIGHT, GAME_WIDTH, toHexColor } from '../config/gameConfig';
import type { RunMode } from './MenuScene';

interface ColdOpenData {
  mode?: RunMode;
}

/** Min time before the "click to continue" hint appears — prevents the
 *  user from accidentally clicking through phases instantly. */
const MIN_HOLD_MS = 600;
/** How quickly Jamie's typing animation reveals characters. */
const JAMIE_CHAR_MS = 28;

interface PhaseObjects {
  objects: Phaser.GameObjects.GameObject[];
  /** Optional async work that should finish before the continue hint shows. */
  ready?: Promise<void>;
}

export class ColdOpenScene extends Phaser.Scene {
  private phaseIndex = 0;
  private hintText!: Phaser.GameObjects.Text;
  private currentObjects: Phaser.GameObjects.GameObject[] = [];
  private acceptingInput = false;
  private mode: RunMode = 'core';
  private readonly onAdvance = (): void => this.advance();

  constructor() {
    super({ key: 'ColdOpenScene' });
  }

  create(data?: ColdOpenData): void {
    this.mode = data?.mode ?? 'core';
    this.phaseIndex = 0;
    this.acceptingInput = false;

    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black).setOrigin(0, 0);

    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 40,
      'click anywhere or press ↵ to continue',
      {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(COLORS.textMuted),
      },
    ).setOrigin(0.5).setAlpha(0).setDepth(DEPTHS.teachPanel + 1);

    // Bind input — single advance source, gated by acceptingInput.
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onAdvance);
    this.input.keyboard?.on('keydown-ENTER', this.onAdvance);
    this.input.keyboard?.on('keydown-SPACE', this.onAdvance);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    this.renderPhase();
  }

  private advance(): void {
    if (!this.acceptingInput) return;
    this.acceptingInput = false;
    this.hideHint();
    this.fadeOut(this.currentObjects, () => {
      this.phaseIndex += 1;
      this.renderPhase();
    });
  }

  private renderPhase(): void {
    const phaseFn = this.PHASES[this.phaseIndex];
    if (!phaseFn) {
      // All phases done — go to desktop.
      this.scene.start('DesktopScene', { mode: this.mode });
      return;
    }
    const { objects, ready } = phaseFn();
    this.currentObjects = objects;
    objects.forEach((o) => {
      const target = o as unknown as { setAlpha?: (v: number) => void };
      target.setAlpha?.(0);
    });
    this.tweens.add({ targets: objects, alpha: 1, duration: 350 });

    const showHintAfter = ready ?? Promise.resolve();
    void showHintAfter.then(() => {
      this.time.delayedCall(MIN_HOLD_MS, () => {
        this.acceptingInput = true;
        this.tweens.add({ targets: this.hintText, alpha: 0.6, duration: 400 });
      });
    });
  }

  private fadeOut(objects: Phaser.GameObjects.GameObject[], onDone: () => void): void {
    if (objects.length === 0) {
      onDone();
      return;
    }
    this.tweens.add({
      targets: objects,
      alpha: 0,
      duration: 220,
      onComplete: () => {
        objects.forEach((o) => o.destroy());
        onDone();
      },
    });
  }

  private hideHint(): void {
    this.tweens.add({ targets: this.hintText, alpha: 0, duration: 150 });
  }

  /** The 5-phase narrative cold-open. Each phase returns the GameObjects it
   *  rendered, plus an optional `ready` promise (used by the typing-effect
   *  phase so the continue hint waits for the type-out to finish). */
  private readonly PHASES: ReadonlyArray<() => PhaseObjects> = [
    // Phase 1 — black, "first month-end close" eyebrow.
    () => {
      const eyebrow = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, 'a fictional desk job, in 22 minutes', {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      const setup = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 4, 'AR RUSH', {
        fontFamily: FONTS.ui,
        fontSize: '40px',
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      return { objects: [eyebrow, setup] };
    },

    // Phase 2 — laptop boot.
    () => {
      const boot = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Cascade OS', {
        fontFamily: FONTS.mono,
        fontSize: '32px',
        color: toHexColor(COLORS.textPrimary),
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      const dots = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 36, '...', {
        fontFamily: FONTS.mono,
        fontSize: '14px',
        color: toHexColor(COLORS.textMuted),
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      return { objects: [boot, dots] };
    },

    // Phase 3 — login welcome.
    () => {
      const welcome = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 24, 'welcome back', {
        fontFamily: FONTS.ui,
        fontSize: '16px',
        color: toHexColor(COLORS.textMuted),
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      const email = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 8, 'you@cascadefoods.com', {
        fontFamily: FONTS.mono,
        fontSize: '20px',
        color: toHexColor(COLORS.textPrimary),
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      return { objects: [welcome, email] };
    },

    // Phase 4 — HR welcome email.
    () => {
      const subject = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 24, 'Welcome to your first month-end close!', {
        fontFamily: FONTS.ui,
        fontSize: '20px',
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      const from = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 8, 'from: hr@cascadefoods.com', {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textMuted),
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      return { objects: [subject, from] };
    },

    // Phase 5 — Jamie's first Slack message types out.
    () => {
      const header = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 36, 'Jamie · Slack', {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);
      const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
        fontFamily: FONTS.ui,
        fontSize: '15px',
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: GAME_WIDTH - 200, useAdvancedWrap: true },
        align: 'center',
      }).setOrigin(0.5).setDepth(DEPTHS.teachPanel);

      const fullText = this.mode === 'endless'
        ? "alright, back at it. quarter-end this time. no training wheels."
        : "morning! ok so — first close. breathe. open the AR Aging window — that's where you start.";

      const ready = new Promise<void>((resolve) => {
        let shown = 0;
        const timer = this.time.addEvent({
          delay: JAMIE_CHAR_MS,
          repeat: fullText.length - 1,
          callback: () => {
            shown += 1;
            body.setText(fullText.slice(0, shown));
            if (shown === fullText.length) {
              timer.remove();
              resolve();
            }
          },
        });
      });

      return { objects: [header, body], ready };
    },
  ];

  private teardown(): void {
    this.input.off(Phaser.Input.Events.POINTER_DOWN, this.onAdvance);
    this.input.keyboard?.off('keydown-ENTER', this.onAdvance);
    this.input.keyboard?.off('keydown-SPACE', this.onAdvance);
  }
}
